import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, Building2, AlertTriangle, ExternalLink } from 'lucide-react';
import DocumentMarks from './DocumentMarks';
import { api } from '../../services/apiService';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * WHO THIS ORDER IS FOR, and what they have sent us.
 *
 * The order row carries a `locationId` and nothing else legible — which is why
 * this screen could not answer "what store is this, and are their documents
 * in?" without opening two other pages.
 *
 * The compliance pack can be UPLOADED and CLEARED here, but never approved. It
 * is verified on the location's Registrations tab only; judging the same
 * document on two screens is how two people reach two answers.
 */

interface LocationRequest {
  requestId: string;
  locationId: string;
  tenantId: string;
  name?: string;
  status?: string;
  legalEntityName?: string;
  ein?: string;
  phone?: string;
  timezone?: string;
  type?: string;
  address?: any;
  documents?: { documentId: string; name: string; status: string; required?: boolean; uploadedAt?: number; comment?: string }[];
}

const oneLine = (a: any): string => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return [a.street || a.line1, a.city, a.state, a.zip || a.postalCode].filter(Boolean).join(', ');
};

export interface LocationContext {
  request: LocationRequest | null;
  location: any | null;
  /**
   * How many stores this tenant has, and when this one was created.
   *
   * Free here: the card already lists every request for the tenant to find
   * this one, so counting them costs nothing and is what tells the order
   * screen whether this is a new business, a new store, or an established
   * store buying more.
   */
  locationCount: number;
  locationCreatedAt?: number;
}

const OrderLocationCard: React.FC<{
  tenantId: string;
  locationId: string;
  tenantName?: string;
  onLoaded?: (ctx: LocationContext) => void;
  /**
   * The parent's live copy of the pack. When given it wins over this card's
   * own load, so an upload in the compliance stage and one here are the same
   * change on screen — two copies of one pack must never disagree.
   */
  documents?: LocationRequest['documents'];
  onUpload?: (documentId: string, file: File) => Promise<void>;
  onDelete?: (documentId: string) => Promise<void>;
  uploading?: string | null;
}> = ({ tenantId, locationId, tenantName, onLoaded, documents, onUpload, onDelete, uploading }) => {
  const [request, setRequest] = useState<LocationRequest | null>(null);
  const [location, setLocation] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataVersion = useDataVersion();

  const load = useCallback(async () => {
    setError(null);
    try {
      // Requests are listed per tenant and matched on locationId: an order
      // carries the LOCATION, never the request that created it.
      const data: any = await api.locationBilling.listTenantRequests(tenantId);
      const rows: LocationRequest[] = Array.isArray(data) ? data : data?.items || data?.requests || [];
      const found = rows.find((r) => r.locationId === locationId) || null;
      setRequest(found);

      let full: any = null;
      if (found?.requestId) {
        try {
          const res: any = await api.locations.getFullLocation(found.requestId, tenantId);
          full = res?.location || null;
          setLocation(full);
        } catch { /* address falls back to the request's own copy */ }
      }
      onLoaded?.({
        request: found,
        location: full,
        locationCount: rows.length,
        locationCreatedAt: (found as any)?.createdAt,
      });
    } catch (e: any) {
      setError(e?.message || 'Could not read this store.');
    } finally {
      setLoading(false);
    }
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, locationId, onLoaded, dataVersion]);

  useEffect(() => { load(); }, [load]);

  const openDoc = async (documentId: string) => {
    if (!request) return;
    try {
      const res: any = await api.locationBilling.getDocumentViewUrl(request.requestId, tenantId, documentId);
      const url = res?.url || res?.viewUrl;
      if (url) window.open(url, '_blank', 'noopener');
    } catch { /* the button simply does nothing rather than throwing at them */ }
  };

  if (loading) {
    return (
      <RubyLoader variant="section" className="bg-white rounded-2xl shadow-sm border border-stone-200/60" />
    );
  }

  if (error || !request) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle size={17} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-stone-800">No store record for this order</p>
            <p className="text-xs text-stone-500 mt-1">
              {error || `Nothing matches location ${locationId}. The order can still be fulfilled, but nobody can see whose it is.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const docs = documents || request.documents || [];
  const required = docs.filter((d) => d.required !== false);
  const verified = required.filter((d) => d.status === 'verified' || d.status === 'accepted');
  const address = oneLine(location?.address || request.address);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-stone-900 truncate">
            {request.name || location?.name || 'Unnamed store'}
          </h3>
          <p className="text-[12px] text-stone-500 mt-0.5">{tenantName || tenantId}</p>
        </div>
        <a
          href={`#/tenant/${encodeURIComponent(tenantId)}/location/${encodeURIComponent(request.requestId)}?tab=registrations`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 shrink-0"
        >
          Registrations <ExternalLink size={11} />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
        <p className="text-[12.5px] text-stone-600 flex items-start gap-1.5">
          <MapPin size={12} className="shrink-0 mt-[3px] text-stone-400" />
          <span className="min-w-0">{address || 'No address on file'}</span>
        </p>
        <p className="text-[12.5px] text-stone-600 flex items-start gap-1.5">
          <Building2 size={12} className="shrink-0 mt-[3px] text-stone-400" />
          <span className="min-w-0">
            {request.legalEntityName || 'Legal entity not set'}
            {request.ein
              ? <span className="font-mono text-stone-500"> · {request.ein}</span>
              : <span className="text-amber-700"> · EIN not on file</span>}
          </span>
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h4 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">
          Business documents
        </h4>
        <span className={`text-[12px] font-bold ${verified.length === required.length ? 'text-emerald-700' : 'text-amber-700'}`}>
          {verified.length}<span className="text-stone-400 font-normal">/{required.length}</span>
        </span>
      </div>

      <DocumentMarks
        documents={docs as any}
        onOpen={openDoc}
        onUpload={onUpload}
        onDelete={onDelete}
        uploading={uploading}
      />

      <p className="text-[11px] text-stone-400 mt-2.5 leading-relaxed">
        Uploads land as awaiting review · approved on Registrations · none of these hold up a shipment.
      </p>
    </div>
  );
};

export default OrderLocationCard;
